export class GraphicPrimitive {
  static threadImage = null;
  static tintedThreadCanvases = new Map();
  static realityMode = true;

  static loadThreadImage() {
    return new Promise((resolve) => {
      if (this.threadImage) {
        resolve(this.threadImage);
        return;
      }
      const img = new Image();
      img.onload = () => {
        console.log("Thread image loaded successfully");
        this.threadImage = img;
        this.tintedThreadCanvases.clear(); // Clear cache to apply new rendering logic
        resolve(img);
      };
      img.onerror = () => {
        console.error("Failed to load thread image from /thread.png");
        resolve(null);
      };
      img.src = '/thread2.png';
    });
  }
  static getTintedThread(color) {
    if (!this.threadImage) {
      return null;
    }
    if (!color) color = 'black';

    if (this.tintedThreadCanvases.has(color)) {
      return this.tintedThreadCanvases.get(color);
    }

    // Create a canvas for the tinted thread
    const canvas = document.createElement('canvas');
    canvas.width = this.threadImage.width;
    canvas.height = this.threadImage.height;
    const ctx = canvas.getContext('2d');

    // Draw the original thread image
    ctx.drawImage(this.threadImage, 0, 0);

    // Get image data to process
    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imgData.data;

    // Parse the target color
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = tempCanvas.height = 1;
    const tempCtx = tempCanvas.getContext('2d');
    tempCtx.fillStyle = color;
    tempCtx.fillRect(0, 0, 1, 1);
    const colorData = tempCtx.getImageData(0, 0, 1, 1).data;
    const targetR = colorData[0];
    const targetG = colorData[1];
    const targetB = colorData[2];

    // Process each pixel
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const brightness = (r + g + b) / 3;

      // Remove white/light background (make transparent)
      if (brightness > 240) {
        data[i + 3] = 0;
      } else if (brightness > 200) {
        // Smooth transition at edges
        const alpha = (240 - brightness) / 40;
        data[i + 3] = Math.round(255 * alpha);

        // Apply target color with shading based on original brightness
        const shadeFactor = brightness / 255;
        data[i] = Math.round(targetR * shadeFactor);
        data[i + 1] = Math.round(targetG * shadeFactor);
        data[i + 2] = Math.round(targetB * shadeFactor);
      } else {
        // Keep full opacity for thread pixels
        data[i + 3] = 255;

        // Apply target color with shading based on original brightness
        // This preserves the texture/depth of the original thread
        const shadeFactor = brightness / 200; // Normalize to expected thread brightness
        data[i] = Math.round(targetR * shadeFactor);
        data[i + 1] = Math.round(targetG * shadeFactor);
        data[i + 2] = Math.round(targetB * shadeFactor);
      }
    }

    ctx.putImageData(imgData, 0, 0);

    this.tintedThreadCanvases.set(color, canvas);
    console.log("Created tinted thread canvas for", color);
    return canvas;
  }

  // trivial case  static getTintedThread(color) {
  //   if (!this.threadImage) {
  //     return null;
  //   }

  //   // Return the original thread image for all colors
  //   // (keeping the method signature for compatibility)
  //   return this.threadImage;
  // }


  constructor(color, fillColor = 'lightred', fillType = 'parallel', spacing = 2, angle = 45, segmentLength = 5, contourStitchType = 'simple', typeName = 'GraphicPrimitive') {
    this.isSelected = false;
    this._color = color;
    this._fillColor = fillColor;
    this._fillType = fillType;
    this._spacing = spacing;
    this._angle = angle;
    this._segmentLength = segmentLength;
    this._contourStitchType = contourStitchType;
    this.typeName = typeName;
    this.lineWidth = 1;
    this.stitchLists = []; // Ordered lists of points [ {x, y}, ... ] for stitches
  }

  // Getters and setters for fill properties that trigger stitch regeneration
  get color() { return this._color; }
  set color(value) {
    if (this._color !== value) {
      this._color = value;
      if (this.regenerateStitches) this.regenerateStitches();
    }
  }

  get fillColor() { return this._fillColor; }
  set fillColor(value) {
    if (this._fillColor !== value) {
      this._fillColor = value;
      if (this.regenerateStitches) this.regenerateStitches();
    }
  }

  get fillType() { return this._fillType; }
  set fillType(value) {
    if (this._fillType !== value) {
      this._fillType = value;
      if (this.regenerateStitches) this.regenerateStitches();
    }
  }

  get spacing() { return this._spacing; }
  set spacing(value) {
    if (this._spacing !== value) {
      this._spacing = value;
      if (this.regenerateStitches) this.regenerateStitches();
    }
  }

  get angle() { return this._angle; }
  set angle(value) {
    if (this._angle !== value) {
      this._angle = value;
      if (this.regenerateStitches) this.regenerateStitches();
    }
  }

  get segmentLength() { return this._segmentLength; }
  set segmentLength(value) {
    if (this._segmentLength !== value) {
      this._segmentLength = value;
      if (this.regenerateStitches) this.regenerateStitches();
    }
  }

  get contourStitchType() { return this._contourStitchType; }
  set contourStitchType(value) {
    if (this._contourStitchType !== value) {
      this._contourStitchType = value;
      if (this.regenerateStitches) this.regenerateStitches();
    }
  }

  toJSON() {
    return {
      type: this.typeName,
      color: this.color,
      fillColor: this.fillColor,
      fillType: this.fillType,
      spacing: this.spacing,
      angle: this.angle,
      segmentLength: this.segmentLength,
      contourStitchType: this.contourStitchType,
      lineWidth: this.lineWidth
    };
  }
  getControlPoints() {
    return [];
  }

  renderDrawing(ctx, transform) {
    throw new Error("renderDrawing method must be implemented");
  }

  /**
   * Render all stitches stored in stitchLists.
   * A stitch is the vector between two sequential points in a list.
   */
  drawStitches(ctx, transform, globalStitchIndex = Infinity, stitchOffset = 0) {
    if (!this.stitchLists || this.stitchLists.length === 0) return;

    ctx.save();

    let currentGlobalCount = stitchOffset;

    this.stitchLists.forEach(list => {
      if (list.length < 2) return;
      const listColor = list.color || this.fillColor || this.color;
      const thread = GraphicPrimitive.realityMode ? GraphicPrimitive.getTintedThread(listColor) : null;

      if (thread) {
        for (let i = 0; i < list.length - 1; i++) {
          if (currentGlobalCount >= globalStitchIndex) break;

          const p1 = list[i];
          const p2 = list[i + 1];

          const dx = p2.x - p1.x;
          const dy = p2.y - p1.y;
          const len = Math.sqrt(dx * dx + dy * dy);
          if (len <= 0) {
            currentGlobalCount++;
            continue;
          }
          const angle = Math.atan2(dy, dx);

          ctx.save();
          ctx.translate(p1.x, p1.y);
          ctx.rotate(angle);

          const thickness = 1;
          const srcX = 0;
          const srcY = 0;
          const srcW = thread.width;
          const srcH = thread.height;

          ctx.imageSmoothingEnabled = true;
          ctx.drawImage(thread,
            srcX, srcY, srcW, srcH,
            0, -thickness / 2, len, thickness
          );

          const highlightLen = Math.max(1, Math.floor(len * 0.2));
          if (highlightLen > 0) {
            const startX = len - highlightLen;
            const grad = ctx.createLinearGradient(startX, 0, len, 0);
            grad.addColorStop(0, 'rgba(255,255,255,0)');
            grad.addColorStop(1, 'rgba(255,255,255,0.2)');
            ctx.save();
            ctx.globalCompositeOperation = 'lighter';
            ctx.fillStyle = grad;
            ctx.fillRect(startX, -thickness / 2, highlightLen, thickness);
            ctx.restore();
          }

          ctx.restore();
          currentGlobalCount++;
        }
      } else {
        ctx.save();
        ctx.strokeStyle = listColor;
        ctx.lineWidth = 1 / transform.scale;
        ctx.beginPath();
        let started = false;
        for (let i = 0; i < list.length - 1; i++) {
          if (currentGlobalCount >= globalStitchIndex) break;
          const p1 = list[i];
          const p2 = list[i + 1];
          if (!started) {
            ctx.moveTo(p1.x, p1.y);
            started = true;
          }
          ctx.lineTo(p2.x, p2.y);
          currentGlobalCount++;
        }
        ctx.stroke();
        ctx.restore();
      }
      
      // If we didn't finish the list due to globalStitchIndex, we still need to advance currentGlobalCount correctly for next lists
      // Actually, currentGlobalCount should represent how many stitches of THIS object we processed.
      // But the loop above stops early. Let's make sure we don't double count if we just returned.
    });

    // Optional: Draw markers with lower opacity
    ctx.save();
    ctx.globalAlpha = 0.4;
    let markerCount = stitchOffset;
    this.stitchLists.forEach(list => {
      const listColor = list.color || this.fillColor || this.color;
      ctx.fillStyle = listColor;
      for (let i = 0; i < list.length; i++) {
        if (markerCount > globalStitchIndex) break;
        ctx.beginPath();
        ctx.arc(list[i].x, list[i].y, 0.4 / transform.scale, 0, 2 * Math.PI);
        ctx.fill();
        markerCount++;
      }
    });
    ctx.restore();

    ctx.restore();
  }


  /**
   * Generates parallel lines (stitches) filling the object.
   * @param {object} bounds - {minX, maxX, minY, maxY}
   * @param {Array} polygon - Output from toPolygon(): [[[x,y], ...], ...]
   *   First ring is outer boundary (CCW), subsequent rings are holes (CW)
   */
  generateParallelStitches(bounds, polygon) {
    this.stitchLists = [];
    if (!polygon || polygon.length === 0) return;

    const { spacing, angle, segmentLength } = this;
    const rad = (angle * Math.PI) / 180;
    const cos = Math.cos(-rad);
    const sin = Math.sin(-rad);

    // 1. Transform polygon to local space where fill lines are horizontal
    const transformPoint = (p) => ({
      x: p[0] * cos - p[1] * sin,
      y: p[0] * sin + p[1] * cos
    });

    const localPolys = polygon.map(poly =>
      poly.map(ring => ring.map(transformPoint))
    );

    // 2. Find local bounds
    let lMinY = Infinity, lMaxY = -Infinity;
    localPolys.forEach(poly => poly.forEach(ring => ring.forEach(p => {
      if (p.y < lMinY) lMinY = p.y;
      if (p.y > lMaxY) lMaxY = p.y;
    })));

    // 3. Scanline intersection
    const rows = [];
    const startY = Math.ceil(lMinY / spacing) * spacing;

    for (let y = startY; y <= lMaxY; y += spacing) {
      const allIntersections = [];

      localPolys.forEach((poly, polyIdx) => {
        // Collect ALL intersections with winding direction
        const crossings = [];

        poly.forEach((ring, ringIndex) => {
          for (let i = 0; i < ring.length; i++) {
            const p1 = ring[i];
            const p2 = ring[(i + 1) % ring.length];

            if ((p1.y <= y && p2.y > y) || (p2.y <= y && p1.y > y)) {
              const x = p1.x + (y - p1.y) * (p2.x - p1.x) / (p2.y - p1.y);
              const goingUp = p2.y > p1.y;
              // For outer ring (CCW): going up adds +1, going down adds -1
              // For hole ring (CW): going up adds -1, going down adds +1
              const isOuter = ringIndex === 0;
              const delta = (goingUp === isOuter) ? 1 : -1;
              crossings.push({ x, delta });
            }
          }
        });

        // Sort by x position
        crossings.sort((a, b) => a.x - b.x);

        // Use winding to find filled segments
        let winding = 0;
        let segStart = null;

        for (const crossing of crossings) {
          const wasInside = winding !== 0;
          winding += crossing.delta;
          const isInside = winding !== 0;

          if (!wasInside && isInside) {
            // Entering filled region
            segStart = crossing.x;
          } else if (wasInside && !isInside) {
            // Exiting filled region
            if (segStart !== null) {
              allIntersections.push(segStart);
              allIntersections.push(crossing.x);
              segStart = null;
            }
          }
        }
      });

      const rowSegments = [];
      for (let i = 0; i < allIntersections.length; i += 2) {
        if (i + 1 < allIntersections.length) {
          const start = { x: allIntersections[i], y: y };
          const end = { x: allIntersections[i + 1], y: y };

          const dx = end.x - start.x;
          const dy = end.y - start.y;
          const length = Math.sqrt(dx * dx + dy * dy);

          if (length <= segmentLength) {
            rowSegments.push([start, end]);
          } else {
            const numSegments = Math.ceil(length / segmentLength);
            const segmentPoints = [];

            for (let j = 0; j <= numSegments; j++) {
              const t = j / numSegments;
              segmentPoints.push({
                x: start.x + dx * t,
                y: start.y + dy * t
              });
            }

            for (let j = 0; j < segmentPoints.length - 1; j++) {
              rowSegments.push([segmentPoints[j], segmentPoints[j + 1]]);
            }
          }
        }
      }

      rows.push(rowSegments);
    }

    // 4. Build stitch lists with alternating direction per row (zig-zag)
    const bCos = Math.cos(rad);
    const bSin = Math.sin(rad);
    const backTransform = (p) => ({
      x: p.x * bCos - p.y * bSin,
      y: p.x * bSin + p.y * bCos
    });

    const concatenated = [];
    for (let r = 0; r < rows.length; r++) {
      const segments = rows[r];
      if (!segments || segments.length === 0) continue;

      const rowPts = [];
      if (r % 2 === 0) {
        for (let s = 0; s < segments.length; s++) {
          rowPts.push(segments[s][0]);
          rowPts.push(segments[s][1]);
        }
      } else {
        for (let s = segments.length - 1; s >= 0; s--) {
          rowPts.push(segments[s][1]);
          rowPts.push(segments[s][0]);
        }
      }

      if (concatenated.length === 0) {
        concatenated.push(...rowPts);
      } else {
        concatenated.push(rowPts[0]);
        for (let k = 1; k < rowPts.length; k++) concatenated.push(rowPts[k]);
      }
    }

    this.stitchLists = concatenated.length ? [concatenated.map(backTransform)] : [];
  }

  static simplifyPoints(points, epsilon = 0.1) {
    if (!points || points.length <= 2) return points;

    let maxDist = 0;
    let index = 0;

    for (let i = 1; i < points.length - 1; i++) {
      // Use the Class name to call the other static helper
      const dist = GraphicPrimitive.getSqSegDist(points[i], points[0], points[points.length - 1]);
      if (dist > maxDist) {
        index = i;
        maxDist = dist;
      }
    }

    if (maxDist > epsilon * epsilon) {
      const res1 = GraphicPrimitive.simplifyPoints(points.slice(0, index + 1), epsilon);
      const res2 = GraphicPrimitive.simplifyPoints(points.slice(index), epsilon);
      return res1.slice(0, res1.length - 1).concat(res2);
    } else {
      return [points[0], points[points.length - 1]];
    }
  }

  static getSqSegDist(p, p1, p2) {
    let x = p1[0], y = p1[1], dx = p2[0] - x, dy = p2[1] - y;
    if (dx !== 0 || dy !== 0) {
      let t = ((p[0] - x) * dx + (p[1] - y) * dy) / (dx * dx + dy * dy);
      if (t > 1) { x = p2[0]; y = p2[1]; }
      else if (t > 0) { x += dx * t; y += dy * t; }
    }
    dx = p[0] - x; dy = p[1] - y;
    return dx * dx + dy * dy;
  }

  /**
   * Helper method to draw scalar or parallel fills.
   * @param {CanvasRenderingContext2D} ctx 
   * @param {object} transform 
   * @param {Function} pathCallback - Function that defines the path (e.g., ctx.rect(...))
   * @param {object} bounds - Optional bounds {minX, maxX, minY, maxY} for optimization
   */
  drawFill(ctx, transform, pathCallback, bounds, globalStitchIndex, stitchOffset) {
    // Temporarily disabled solid fill to avoid conflicts with stitches
  

    if (this.fillType === 'parallel') {
      this.drawStitches(ctx, transform, globalStitchIndex, stitchOffset);
    }
    else
    {
      if (this.fillColor) {
        ctx.save();
        ctx.beginPath();
        pathCallback();
        ctx.fillStyle = this.fillColor;
        ctx.fill();
        ctx.restore();
      }

    }
  }

  AddPoint(point) {

    return false;
  }

  AddSecondPoint(point) {

    return false;
  }

  generateContourStitches(multiPoly) {
    if (!multiPoly || multiPoly.length === 0) return [];

    const contourLists = [];
    for (const poly of multiPoly) {
      for (const ring of poly) {
        if (!ring || ring.length < 2) continue;

        // Convert ring to a path of points
        const path = ring.map(p => ({ x: p[0], y: p[1] }));
        if (path.length > 2 && (path[0].x !== path[path.length - 1].x || path[0].y !== path[path.length - 1].y)) {
          path.push(path[0]); // Close the loop for polygons
        }

        let totalLength = 0;
        for (let i = 0; i < path.length - 1; i++) {
          totalLength += Math.hypot(path[i+1].x - path[i].x, path[i+1].y - path[i].y);
        }

        if (totalLength < 0.5 * this.segmentLength) {
          if (totalLength > 0) {
             const chain = [path[0], path[path.length-1]];
             chain.color = this.color;
             contourLists.push(chain);
          }
          continue;
        }
        
        const numStitches = Math.max(1, Math.round(totalLength / this.segmentLength));
        const actualStitchLength = totalLength / numStitches;

        const chain = [path[0]];
        let distanceTraveled = 0;
        let segmentIndex = 0;

        for (let i = 1; i <= numStitches; i++) {
          const nextStitchDist = i * actualStitchLength;
          
          // Find the segment where the next stitch falls
          while(segmentIndex < path.length - 1) {
            const p1 = path[segmentIndex];
            const p2 = path[segmentIndex+1];
            const segLength = Math.hypot(p2.x - p1.x, p2.y - p1.y);

            if (distanceTraveled + segLength >= nextStitchDist) {
              const distIntoSeg = nextStitchDist - distanceTraveled;
              const t = segLength === 0 ? 0 : distIntoSeg / segLength;
              const newPoint = {
                x: p1.x + (p2.x - p1.x) * t,
                y: p1.y + (p2.y - p1.y) * t,
              };
              chain.push(newPoint);
              break; // Found the stitch, move to next i
            } else {
              distanceTraveled += segLength;
              segmentIndex++;
            }
          }
        }
        
        if (chain.length > 0) {
          chain.color = this.color;
          contourLists.push(chain);
        }
      }
    }
    return contourLists;
  }

  setIsDrawing(dr) { }

  isDrawing() {
    return false
  }

  mouseUp() {
    return false;
    //throw new Error("AddPoint method must be implemented");
  }

  move(dx, dy) {
    throw new Error("move method must be implemented");
  }

  // Method to render a preview of the drawing
  renderPreview(ctx, transform) {
    throw new Error("renderPreview method must be implemented");
  }

  // Method to handle mouse events
  handleMouseEvent(event) {
    throw new Error("handleMouseEvent method must be implemented");
  }

  // Get bounding box - uses control points by default, can be overridden
  getBounds() {
    const controlPoints = this.getControlPoints();
    if (!controlPoints || controlPoints.length === 0) return null;
    
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    controlPoints.forEach(p => {
      if (p.x !== undefined) minX = Math.min(minX, p.x);
      if (p.x !== undefined) maxX = Math.max(maxX, p.x);
      if (p.y !== undefined) minY = Math.min(minY, p.y);
      if (p.y !== undefined) maxY = Math.max(maxY, p.y);
    });
    
    return { minX, maxX, minY, maxY };
  }
}
